import type * as React from 'react'
import { useState } from 'react'

import clsx from 'clsx'

import {
  Button,
  DropdownMenu,
  NewBadge,
  Tooltip,
  ChevronDownIcon,
  PlusIcon,
  Flex,
} from '@buffer-mono/popcorn'

import { SearchImagesModal } from './components/search-images-modal/SearchImagesModal'
import type { IntegrationsContext, IntegrationsService } from './types'

import styles from './IntegrationsBar.module.css'

/**
 *  COMPONENT: IDEAS INTEGRATIONS BAR
 */
export const IntegrationsBar = ({
  context,
  services,
  activeService,
  disableBar = false,
  container,
  children,
}: {
  context: IntegrationsContext
  services: Array<IntegrationsService>
  activeService: IntegrationsService | null
  disableBar?: boolean
  container?: HTMLElement | null
  children?: React.ReactNode
}): JSX.Element | null => {
  const [lastIntegrationUsed, setLastIntegrationUsed] = useState(
    localStorage.getItem('last_used_integration'),
  )

  const [sawNewIndicator, setSawNewIndicator] = useState(
    !!localStorage.getItem('integration_bar_seen_new_indicator'),
  )

  if (services.length === 0 && !children) return null

  const storedIntegration = services.find(
    (e) => e.title === lastIntegrationUsed,
  )

  const hasNewTags = !!services.find((e) => e.isNew) && !sawNewIndicator

  const tooltipLabel = disableBar
    ? 'No more media can be attached'
    : 'Select media from integrations'

  // @ts-expect-error TS(7006) FIXME: Parameter 'service' implicitly has an 'any' type.
  const onServiceClick = (service): void => {
    localStorage.setItem('last_used_integration', service.title)
    setLastIntegrationUsed(service.title)
    service.onClick?.()
  }

  const removeNewIndicator = (): void => {
    localStorage.setItem('integration_bar_seen_new_indicator', 'true')
    setSawNewIndicator(true)
  }

  return (
    <>
      <div
        className={styles.integrationsBarContainer}
        data-testid="integrations-bar"
      >
        <Button.Group variant="tertiary">
          {storedIntegration && (
            <Tooltip
              content={
                disableBar
                  ? 'No more media can be attached'
                  : storedIntegration.label
              }
            >
              <Button
                onClick={(): void => storedIntegration?.onClick?.()}
                disabled={disableBar}
                aria-label={storedIntegration.label}
                className={styles.mainButton}
              >
                {storedIntegration?.icon}
              </Button>
            </Tooltip>
          )}

          <DropdownMenu onOpenChange={removeNewIndicator}>
            <Tooltip content={tooltipLabel}>
              <DropdownMenu.Trigger disabled={disableBar}>
                <Button
                  aria-label="More integration options"
                  id="grouped-integrations-button"
                  data-testid="grouped-integrations-button"
                  disabled={disableBar}
                  className={clsx(
                    styles.dropdownTrigger,
                    hasNewTags && styles.hasNewTags,
                    !storedIntegration && styles.addMediaIconShown,
                  )}
                >
                  {!storedIntegration && (
                    <PlusIcon className={styles.addMediaIcon} />
                  )}
                  <ChevronDownIcon />
                </Button>
              </DropdownMenu.Trigger>
            </Tooltip>

            <DropdownMenu.Content align="start">
              {services.map((service) => (
                <DropdownMenu.Item
                  onClick={(): void => onServiceClick(service)}
                  key={service.id}
                  data-testid={`integrations-bar-${service.id}`}
                  disabled={disableBar}
                >
                  <Flex align="center" gap="2xs">
                    {service.icon} {service.title}
                  </Flex>
                  {service.isNew && <NewBadge />}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu>
        </Button.Group>

        {children && <div className={styles.childrenArea}>{children}</div>}
      </div>

      {/* GENERAL MODAL FOR INTEGRATIONS */}
      <SearchImagesModal
        open={!!activeService?.openSearchImagesModal}
        handleClose={(): void => activeService?.onClose?.()}
        service={activeService}
        context={context}
        container={container}
      />
    </>
  )
}

IntegrationsBar.defaultProps = {
  disableBar: false,
  children: null,
}
